import * as React from 'react';
import {
  MasonryEngineNode,
  MasonryEngineNodeAction,
  MasonryEngineNodeRendererProps,
  useMasonryEngineActionEmitter,
} from '@leagueplatform/masonry-engine';
import { ChatSessionItem } from '../../../components/chat-session-item/chat-session-item.component';
import { useSelectedSessionId } from '../../chat-selection-store';

export const NODE_TYPE_CHAT_SESSION_ITEM = 'chatSessionItem';

type ChatSessionItemProps = {
  date: string;
  time: string;
  title: string;
  message?: string;
  isSelected?: boolean;
  /** Stable identifier; compared against the list's selection store. */
  sessionId?: string;
};

type ChatSessionItemActions = {
  onClick?: MasonryEngineNodeAction;
  onLoad?: MasonryEngineNodeAction;
};

type ChatSessionItemNode = MasonryEngineNode<
  typeof NODE_TYPE_CHAT_SESSION_ITEM,
  ChatSessionItemProps,
  ChatSessionItemActions
>;

export type ChatSessionItemRendererProps =
  MasonryEngineNodeRendererProps<ChatSessionItemNode>;

export const ChatSessionItemRenderer = ({
  date,
  time,
  title,
  message,
  isSelected,
  sessionId,
  onClick,
  rootElementRef,
}: ChatSessionItemRendererProps): React.ReactElement => {
  const invokeAction = useMasonryEngineActionEmitter();
  const selectedSessionId = useSelectedSessionId();

  // The store is authoritative once a selection has been made; fall
  // back to the masonry `isSelected` prop before that to honor the
  // backend's `autoSelectFirst`. Consequence: after any selection
  // exists in the store, `isSelected` from masonry is ignored for
  // items with a `sessionId`. The store is reset on list unmount
  // (see `registerChatListMount`), which is the only way the prop
  // fallback takes over again.
  const effectiveIsSelected =
    selectedSessionId !== null && sessionId !== undefined
      ? selectedSessionId === sessionId
      : isSelected;

  const handleClick = React.useCallback(
    (event: React.SyntheticEvent) => {
      if (onClick) {
        invokeAction({ event, ...onClick });
      }
    },
    [invokeAction, onClick],
  );

  return (
    <ChatSessionItem
      ref={rootElementRef as React.Ref<HTMLButtonElement>}
      date={date}
      time={time}
      title={title}
      message={message}
      isSelected={effectiveIsSelected}
      onClick={handleClick}
    />
  );
};
